/**
 * Stock Movements Hooks
 * @see US-11.8 - Page Mouvements Stock
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getStockMovements,
  getStockMovement,
  createStockMovement,
  getCurrentStock,
  getProductStockHistory,
  generatePOSMovements,
} from '../services/stock-movements.service';
import type {
  QueryStockMovementsDto,
  QueryStockDto,
  CreateStockMovementDto,
  GeneratePOSMovementsDto,
} from '@bstock/shared';

/**
 * Query key factory for stock movements
 */
export const stockMovementsKeys = {
  all: ['stock-movements'] as const,
  lists: () => [...stockMovementsKeys.all, 'list'] as const,
  list: (filters: QueryStockMovementsDto) =>
    [...stockMovementsKeys.lists(), filters] as const,
  details: () => [...stockMovementsKeys.all, 'detail'] as const,
  detail: (id: string) => [...stockMovementsKeys.details(), id] as const,
  stock: () => ['stock'] as const,
  currentStock: (filters: QueryStockDto) =>
    [...stockMovementsKeys.stock(), 'current', filters] as const,
  productHistory: (rawProductId: string) =>
    [...stockMovementsKeys.stock(), 'history', rawProductId] as const,
};

/**
 * Hook to get list of stock movements
 */
export function useStockMovements(params: QueryStockMovementsDto = {}) {
  return useQuery({
    queryKey: stockMovementsKeys.list(params),
    queryFn: () => getStockMovements(params),
  });
}

/**
 * Hook to get a single movement
 */
export function useStockMovement(id: string) {
  return useQuery({
    queryKey: stockMovementsKeys.detail(id),
    queryFn: () => getStockMovement(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a manual movement
 */
export function useCreateStockMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStockMovementDto) => createStockMovement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockMovementsKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementsKeys.stock() });
      toast.success('Mouvement cree avec succes');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erreur lors de la creation du mouvement');
    },
  });
}

/**
 * Hook to get current stock
 */
export function useCurrentStock(params: QueryStockDto = {}) {
  return useQuery({
    queryKey: stockMovementsKeys.currentStock(params),
    queryFn: () => getCurrentStock(params),
  });
}

/**
 * Hook to get stock for a specific product
 */
export function useProductStock(rawProductId: string) {
  return useQuery({
    queryKey: stockMovementsKeys.currentStock({ rawProductId }),
    queryFn: () => getCurrentStock({ rawProductId }),
    enabled: !!rawProductId,
    select: (data) => data.data[0], // Return single product
  });
}

/**
 * Hook to get stock history for a product
 */
export function useProductStockHistory(rawProductId: string, page = 1, limit = 10) {
  return useQuery({
    queryKey: [...stockMovementsKeys.productHistory(rawProductId), page, limit],
    queryFn: () => getProductStockHistory(rawProductId, page, limit),
    enabled: !!rawProductId,
  });
}

/**
 * Hook to generate POS movements
 */
export function useGeneratePOSMovements() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: GeneratePOSMovementsDto) => generatePOSMovements(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: stockMovementsKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementsKeys.stock() });
      if (result.dryRun) {
        toast.success(`${result.generated} mouvements seraient generes (mode simulation)`);
      } else {
        toast.success(`${result.generated} mouvements generes avec succes`);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erreur lors de la generation des mouvements');
    },
  });
}
